import { z } from 'zod';
import { isoTimestamp } from '../schemas/common.js';
/**
 * The weekly fruit/veg box ("pakket"), read from the farm's website and shown on
 * its own screen.
 *
 * Sourced from a public WordPress REST endpoint through a **server proxy** — the
 * browser cannot call it directly, because the CSP pins `connect-src` to `'self'`
 * (see the Caddyfile), exactly like the weather widget. The server fetches the
 * latest "Pakketinhoud week N" post, parses its messy HTML into this shape and
 * validates it on the way out, so a change in the site's markup fails there
 * rather than as `undefined` on the screen.
 *
 * A post lists one *or more* box types, each its own paragraph. Within a box the
 * size tiers are **cumulative and additive**: `base` is what a Klein box holds,
 * `middel` is what Middel *adds* on top, `groot` is what Groot adds on top of
 * that (the parser stores the additions, not the running totals — `compose.ts`
 * builds the cumulative list). `raw` is the paragraph's plain text, kept so the
 * screen can fall back to it when the tier-splitting finds nothing.
 */
export const pakketSection = z.object({
    /** The box-type name (e.g. "Combi", "Aanvullend"), or null for an unlabelled list. */
    label: z.string().nullable(),
    /** The Klein/base items — everything before "Middel ook…". */
    base: z.array(z.string()),
    /** What Middel *adds* to the base ("Middel ook …"). */
    middel: z.array(z.string()),
    /** What Groot *adds* on top of Middel ("groot ook …" / "Groter ook …"). */
    groot: z.array(z.string()),
    /** The paragraph as plain text — a display fallback when tier-splitting is empty. */
    raw: z.string(),
});
export const pakketResponse = z.object({
    /** The post title, e.g. "Pakketinhoud week 36". */
    title: z.string(),
    /** The week number parsed from the title, or null if none is stated. */
    week: z.number().int().nullable(),
    /**
     * The post's publication date as the site states it: WordPress local
     * wall-clock `YYYY-MM-DDTHH:mm:ss` with **no** offset — so it is a plain string
     * (not `isoTimestamp`, which requires a `Z`/offset), like `weatherDay.sunset`.
     * The client formats the date it shows.
     */
    published_at: z.string(),
    sections: z.array(pakketSection),
    fetched_at: isoTimestamp,
});
//# sourceMappingURL=schema.js.map